// react
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

// components
import PaymentModal from "./PaymentModal";
import BasketItem from "./BasketItem";
import CheckoutSkeleton from "./CheckoutSkeleton";
import { Button, Popup, Icon } from "semantic-ui-react";
import { authService } from "../../store/services/ServiceAuth";

const summarizeTaxes = (items, configItems) => {
    const taxSummary = {};
    let grandTotalBase = 0;
    let grandTotalTax = 0;
    let grandTotalPrice = 0;

    items.forEach((item) => {
        const amount = configItems.find(config => config.id === item.id)?.amount || 1
        const prices = item.prices;
        const taxRate = prices.tax_rate;
        const isDiscounted = item.is_discounted;

        // Select the correct base and tax amount based on discount status
        const baseAmount = (isDiscounted ? prices.discount_price_base_amount : prices.price_base_amount) * amount;
        const taxAmount = (isDiscounted ? prices.discount_price_tax_amount : prices.price_tax_amount) * amount;
        const totalPrice = baseAmount + taxAmount;

        // Track grand totals
        grandTotalBase += baseAmount;
        grandTotalTax += taxAmount;
        grandTotalPrice += totalPrice;

        // If tax rate doesn't exist in summary, initialize it
        if (!taxSummary[taxRate]) {
            taxSummary[taxRate] = {
                tax_rate: `${taxRate} %`,  // Format as percentage
                total_base_amount: 0,
                total_tax_amount: 0,
                total_price: 0
            };
        }

        // Accumulate values per tax rate
        taxSummary[taxRate].total_base_amount += baseAmount;
        taxSummary[taxRate].total_tax_amount += taxAmount;
        taxSummary[taxRate].total_price += totalPrice;
    });

    // Convert object to sorted array and ensure 2 decimal places as STRING
    const taxList = Object.values(taxSummary).sort((a, b) => parseFloat(a.tax_rate) - parseFloat(b.tax_rate))
        .map(entry => ({
            tax_rate: entry.tax_rate,
            total_base_amount: entry.total_base_amount.toFixed(2),
            total_tax_amount: entry.total_tax_amount.toFixed(2),
            total_price: entry.total_price.toFixed(2) // Ensure EUR formatting
        }));

    // Add final row with grand totals
    taxList.push({
        tax_rate: "Total",
        total_base_amount: grandTotalBase.toFixed(2),
        total_tax_amount: grandTotalTax.toFixed(2),
        total_price: grandTotalPrice.toFixed(2)
    });

    return taxList;
};


// component
const BasketList = ({
    basket,
    basketItems = [],
    items = [],
    setItems,
    countTotal,
    removeProductFromBasket,
    setBasketEvents,
    isLoading,
}) => {
    // state
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
    const user = useSelector((state) => state.user)
    const dispatch = useDispatch()
    const history = useHistory()

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (params.get("continue") !== "pay" || isLoading || !basketItems.length || user.role !== "User") {
            return
        }
        params.delete("continue")
        const search = params.toString()
        history.replace({
            pathname: window.location.pathname,
            search: search ? "?" + search : "",
        })
        setIsPaymentModalOpen(true)
    }, [basketItems.length, history, isLoading, user.role])

    if (isLoading) {
        return <CheckoutSkeleton />;
    }

    if (!basketItems || !items || basketItems.length === 0) {
        return null;
    }

    const sumarizedTaxes = summarizeTaxes(basketItems, items)

    const uniqueProviders = [
        ...new Map(basketItems.map(item => [item.provider.id, item.provider])).values()
    ]

    const providerNames = uniqueProviders.map(provider => provider.name);


    // template
    return (
        <div className="checkout-container">
            <div className="checkout-section checkout-items-section">
                <div className="checkout-section-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "1rem", flexWrap: "wrap" }}>
                    <h2 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>Váš košík</h2>
                    {user && user.email ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontSize: "0.95rem", fontWeight: 500, color: "#666" }}>
                            Prihlásený ako {user.email}
                            <span>
                                (
                                <button
                                    type="button"
                                    onClick={() => {
                                        authService.logout(dispatch)
                                        setIsPaymentModalOpen(false)
                                    }}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        color: "var(--primary)",
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        fontSize: "0.95rem",
                                    }}
                                >
                                    odhlásiť
                                </button>
                                )
                            </span>
                        </span>
                    ) : (
                        <span style={{ fontSize: "0.95rem", fontWeight: 500, color: "#666" }}>
                            Prihlásenie prebehne pri platbe
                        </span>
                    )}
                </div>
                <div className="basket-items-list">
                    {basketItems.map((item, index) => (
                        <BasketItem
                            as="card"
                            index={index}
                            key={item.id}
                            item={item}
                            items={items}
                            setItems={setItems}
                            removeProductFromBasket={removeProductFromBasket}
                        />
                    ))}
                </div>
            </div>

            <div className="checkout-section checkout-summary-section">
                <div className="checkout-summary-content">
                    <div className="checkout-summary-info">
                        <h3 className="checkout-subsection-title">Informácie o objednávke</h3>
                        {providerNames.length > 1 && (
                            <div className="checkout-info-warning-banner">
                                <Icon name="warning circle" />
                                <span><strong>V košíku sa nachádzajú aktivity od viacerích zadávateľov:</strong> {providerNames.join(', ')}</span>
                            </div>
                        )}
                        <div className="checkout-info-rows">
                            <div className="checkout-info-row">
                                <span className="checkout-info-label">Spôsob úhrady</span>
                                <span className="checkout-info-value">Platba kartou (CardPay TB)</span>
                            </div>
                            <div className="checkout-info-row">
                                <span className="checkout-info-label">Spôsob dodania</span>
                                <span className="checkout-info-value" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35em' }}>
                                    <span>Online</span>
                                    <Popup
                                        trigger={<Icon name="info circle" className="info-icon" style={{ position: 'relative', top: '-0.2rem' }} />}
                                        content={<p>Dostupné v klientskom prostredí <strong>"Moje termíny"</strong> po prihlásení</p>}
                                    />
                                </span>
                            </div>
                            <div className="checkout-info-row">
                                <span className="checkout-info-label">Počet položiek</span>
                                <span className="checkout-info-value">{basketItems.length}</span>
                            </div>
                        </div>
                    </div>

                    <div className="checkout-summary-taxes">
                        <h3 className="checkout-subsection-title">Súhrn cien</h3>
                        <div className="checkout-tax-summary">
                            <div className="checkout-tax-row checkout-tax-header">
                                <div className="checkout-tax-row-left">
                                    <span className="checkout-tax-rate">Sadzba</span>
                                </div>
                                <div className="checkout-tax-row-right">
                                    <span className="checkout-tax-amount">Základ</span>
                                    <span className="checkout-tax-amount">DPH</span>
                                    <span className="checkout-tax-amount">Suma</span>
                                </div>
                            </div>
                            {sumarizedTaxes.map((tax, idx) => {
                                const isLastRow = idx === sumarizedTaxes.length - 1;
                                return (
                                    <div key={idx}
                                        className={`checkout-tax-row ${isLastRow ? 'checkout-tax-total-row' : ''}`}
                                        style={{ borderTop: isLastRow ? 'none' : '1px solid #f0f0f0' }}>
                                        <div className="checkout-tax-row-left">
                                            <span className="checkout-tax-rate">{tax.tax_rate}</span>
                                        </div>
                                        <div className="checkout-tax-row-right">
                                            <span className="checkout-tax-amount">{tax.total_base_amount} EUR</span>
                                            <span className="checkout-tax-amount">{tax.total_tax_amount} EUR</span>
                                            <span className="checkout-tax-amount checkout-tax-total-amount">{tax.total_price} EUR</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            <div className="checkout-section checkout-payment-section">
                <div className="checkout-payment-wrapper">
                    <p className="checkout-payment-note">
                        Kliknutím na tlačidlo potvrdzujete objednávku a budete presmerovaní na platobnú bránu.
                    </p>
                    <PaymentModal
                        isOpen={isPaymentModalOpen}
                        onClose={() => setIsPaymentModalOpen(false)}
                        onOpen={() => setIsPaymentModalOpen(true)}
                        items={items}
                        setItems={setItems}
                        trigger={
                            <Button
                                type="button"
                                className="checkout-button"
                                color="pink"
                                size="huge"
                                fluid
                            >
                                <Icon name="lock" />
                                Zaplatiť objednávku ({countTotal()} EUR)
                            </Button>
                        }
                        basket_items={basket}
                        removeItem={removeProductFromBasket}
                        setBasketEvents={setBasketEvents}
                    />
                </div>
            </div>
        </div>
    );
};

export default BasketList;
